import heroMockup from './mockups/cover-mockup.png';
import discoverMockup from './mockups/discover-page-mockup.png';
import recipesMockup from './mockups/recipes-page-mockup.png';
import aiRestaurantMockup from './mockups/ai-restaurant-mockup.png';
import mapMockup from './mockups/map-page-mockup.png';
import plateMockup from './mockups/plate-calculator-mockup.png';
import settingsMockup from './mockups/settings-filters-overview-mockup.png';
import turkeyChiliImg from './turkey-chili.jpg';

// Import SVG Icons
import { ReactComponent as BurgerIcon } from './icons/burger.svg';
import { ReactComponent as PizzaIcon } from './icons/pizza.svg';
import { ReactComponent as TacoIcon } from './icons/taco.svg';
import { ReactComponent as SushiIcon } from './icons/sushi.svg';
import { ReactComponent as DonutIcon } from './icons/donut.svg';
import { ReactComponent as CoffeeIcon } from './icons/coffee.svg';
import { ReactComponent as IceCreamIcon } from './icons/ice cream 1.svg';
import { ReactComponent as CroissantIcon } from './icons/croissant.svg';
import { ReactComponent as AvocadoIcon } from './icons/avocado.svg';
import { ReactComponent as AppleIcon } from './icons/apple.svg';
import { ReactComponent as CakeIcon } from './icons/cake 1.svg';
import { ReactComponent as FriesIcon } from './icons/french fries.svg';
import { ReactComponent as ChickenIcon } from './icons/chicken leg.svg';
import { ReactComponent as CherryIcon } from './icons/cherry.svg';
import { ReactComponent as WatermelonIcon } from './icons/watermelon.svg';
import { ReactComponent as StrawberryIcon } from './icons/strawberry.svg';
import { ReactComponent as BananaIcon } from './icons/banana.svg';
import { ReactComponent as FireIcon } from './icons/fire.svg';
import { ReactComponent as ProteinIcon } from './icons/protein.svg';
import { ReactComponent as FatIcon } from './icons/fat.svg';
import { ReactComponent as CarbsIcon } from './icons/carbs.svg';
import { ReactComponent as MeatIcon } from './icons/meat.svg';
import { ReactComponent as BaconIcon } from './icons/bacon.svg';
import { ReactComponent as FishIcon } from './icons/fish 1.svg';

import './App.css';
import { motion, useScroll, useTransform, AnimatePresence, useInView } from 'framer-motion';
import { useRef, useEffect, useState, cloneElement } from 'react';

// Celebration icons for the animation
const celebrationIcons = [
  BurgerIcon, PizzaIcon, TacoIcon, SushiIcon, DonutIcon, 
  IceCreamIcon, CroissantIcon, AppleIcon, CakeIcon, FriesIcon,
  CherryIcon, WatermelonIcon, StrawberryIcon, BananaIcon, AvocadoIcon
];

// Logo.dev API for restaurant brand logos
const LOGO_TOKEN = ['pk_H1C7', 'gyvNSn', '6ov2lt', '6dPxfg'].join('');
const logoUrl = (domain, size = 128) =>
  `https://img.logo.dev/${domain}?token=${LOGO_TOKEN}&size=${size}&format=png`;

const brandLogos = [
  { name: "McDonald's", domain: 'mcdonalds.com' },
  { name: 'Chick-fil-A', domain: 'chick-fil-a.com' },
  { name: 'Chipotle', domain: 'chipotle.com' },
  { name: 'Subway', domain: 'subway.com' },
  { name: 'Starbucks', domain: 'starbucks.com' },
  { name: 'Taco Bell', domain: 'tacobell.com' },
  { name: "Wendy's", domain: 'wendys.com' },
  { name: 'Panda Express', domain: 'pandaexpress.com' },
  { name: 'Panera Bread', domain: 'panerabread.com' },
  { name: "Domino's", domain: 'dominos.com' },
  { name: 'Five Guys', domain: 'fiveguys.com' },
  { name: "Chili's", domain: 'chilis.com' },
  { name: 'Popeyes', domain: 'popeyes.com' },
  { name: 'KFC', domain: 'kfc.com' },
  { name: 'In-N-Out', domain: 'in-n-out.com' },
  { name: 'Burger King', domain: 'bk.com' },
  { name: 'Sonic', domain: 'sonicdrivein.com' },
  { name: 'Pizza Hut', domain: 'pizzahut.com' },
  { name: 'Wingstop', domain: 'wingstop.com' },
  { name: "Raising Cane's", domain: 'raisingcanes.com' },
];

// Email validation helper
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Theme Toggle Hook
const getTimeBasedTheme = () => {
  const hour = new Date().getHours();
  return (hour >= 19 || hour < 7) ? 'dark' : 'light';
};

const useTheme = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('munch-theme');
      const savedAt = localStorage.getItem('munch-theme-set-at');
      if (saved && savedAt) {
        const hoursSince = (Date.now() - Number(savedAt)) / 3600000;
        if (hoursSince < 12) return saved;
      }
      return getTimeBasedTheme();
    }
    return 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      localStorage.setItem('munch-theme', next);
      localStorage.setItem('munch-theme-set-at', String(Date.now()));
      return next;
    });
  };
  
  return { theme, toggleTheme };
};


// Stat Counter Animation
const AnimatedCounter = ({ value, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let start = 0;
          const end = parseInt(value);
          const duration = 2000;
          const increment = end / (duration / 16);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  
  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
};

const useMobileMode = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(hover: none)').matches || window.innerWidth <= 768;
  });
  useEffect(() => {
    const mq = window.matchMedia('(hover: none)');
    const check = () => setIsMobile(mq.matches || window.innerWidth <= 768);
    window.addEventListener('resize', check);
    mq.addEventListener('change', check);
    return () => {
      window.removeEventListener('resize', check);
      mq.removeEventListener('change', check);
    };
  }, []);
  return isMobile;
};

const FeatureCard = ({ preview, title, description, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: '-30% 0px -30% 0px' });
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useMobileMode();
  const isActive = isMobile ? isInView : isHovered;

  return (
    <motion.div
      ref={ref}
      className={`feature-card ${isActive ? 'anim-active' : ''}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      whileHover={{ y: -8, scale: 1.01 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="feature-preview">
        {cloneElement(preview, { active: isActive })}
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      <div className="feature-glow"></div>
    </motion.div>
  );
};

// --- Shared macro pill row (matches the app's Cal/P/F/C pattern) ---
const MacroPills = ({ cal, p, f, c, delay = 0 }) => {
  const items = [
    { label: 'Cal', value: cal, Icon: FireIcon, cls: 'macro-cal' },
    { label: 'P', value: p, Icon: ProteinIcon, cls: 'macro-p' },
    { label: 'F', value: f, Icon: FatIcon, cls: 'macro-f' },
    { label: 'C', value: c, Icon: CarbsIcon, cls: 'macro-c' },
  ];
  return (
    <div className="app-macro-row">
      {items.map((m, i) => (
        <motion.div
          key={m.label}
          className={`app-macro-pill ${m.cls}`}
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: delay + i * 0.08, type: 'spring', stiffness: 400, damping: 20 }}
        >
          <span className="amp-label">{m.label}</span>
          <span className="amp-value">{m.value}</span>
        </motion.div>
      ))}
    </div>
  );
};

// --- Feature Preview Components (mini UI vignettes from the app) ---

const MacroPreview = () => (
  <div className="preview-macros">
    <div className="preview-meal-row">
      <div className="preview-meal-icon"><ChickenIcon /></div>
      <div className="preview-meal-name">grilled nuggets</div>
    </div>
    <div className="app-macro-row macro-wave">
      {[
        { label: 'Cal', value: '130', cls: 'macro-cal' },
        { label: 'P', value: '25', cls: 'macro-p' },
        { label: 'F', value: '3', cls: 'macro-f' },
        { label: 'C', value: '1', cls: 'macro-c' },
      ].map((m, i) => (
        <motion.div
          key={m.label}
          className={`app-macro-pill ${m.cls}`}
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 + i * 0.08, type: 'spring', stiffness: 400, damping: 20 }}
          style={{ animationDelay: `${i * 0.15}s` }}
        >
          <span className="amp-label">{m.label}</span>
          <span className="amp-value">{m.value}</span>
        </motion.div>
      ))}
    </div>
  </div>
);

const filterSets = [
  [
    { value: '< 800', unit: 'cal' },
    { value: '> 20', unit: 'protein' },
    { value: '< 30', unit: 'fat' },
    { value: 'any', unit: 'carbs' },
  ],
  [
    { value: '< 500', unit: 'cal' },
    { value: '> 40', unit: 'protein' },
    { value: 'any', unit: 'fat' },
    { value: '< 50', unit: 'carbs' },
  ],
  [
    { value: '< 2000', unit: 'cal' },
    { value: 'any', unit: 'protein' },
    { value: '< 15', unit: 'fat' },
    { value: '> 100', unit: 'carbs' },
  ],
];

const FiltersPreview = ({ active }) => {
  const icons = [
    { Icon: FireIcon, cls: 'fc-cal' },
    { Icon: ProteinIcon, cls: 'fc-p' },
    { Icon: FatIcon, cls: 'fc-f' },
    { Icon: CarbsIcon, cls: 'fc-c' },
  ];
  const [setIdx, setSetIdx] = useState(0);
  const wasActive = useRef(false);

  useEffect(() => {
    if (!active) {
      wasActive.current = false;
      return;
    }
    if (!wasActive.current) {
      wasActive.current = true;
      setSetIdx(prev => (prev + 1) % filterSets.length);
    }
    const timer = setInterval(() => {
      setSetIdx(prev => (prev + 1) % filterSets.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [active]);

  const current = filterSets[setIdx];

  return (
    <div className="preview-filters">
      <div className="preview-filters-label">nutrition filters</div>
      <div className="preview-filters-row">
        {icons.map((f, i) => (
          <motion.div
            key={f.cls}
            className="preview-filter-circle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.1 }}
          >
            <div className={`filter-circle-icon ${f.cls}`}><f.Icon /></div>
            <AnimatePresence mode="wait">
              <motion.span
                key={`${setIdx}-${i}`}
                className="filter-circle-value"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
              >
                {current[i].value}
              </motion.span>
            </AnimatePresence>
            <span className="filter-circle-unit">{current[i].unit}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const SearchChipsPreview = () => {
  const chips = [
    { label: 'all', active: true, Icon: null },
    { label: 'chicken', active: false, Icon: ChickenIcon },
    { label: 'beef', active: false, Icon: MeatIcon },
    { label: 'pork', active: false, Icon: BaconIcon },
    { label: 'fish', active: false, Icon: FishIcon },
    { label: 'vegan', active: false, Icon: null },
  ];
  return (
    <div className="preview-chips">
      <div className="preview-chips-title">discover</div>
      <div className="preview-chips-outer">
        <div className="preview-chips-row chips-slide">
          {chips.map((c) => (
            <div
              key={c.label}
              className={`preview-chip ${c.active ? 'active' : ''}`}
            >
              {c.Icon && <span className="chip-icon"><c.Icon /></span>}
              {c.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MapPinsPreview = () => {
  const pins = [
    { name: "McDonald's", domain: 'mcdonalds.com', count: 15, x: '12%', y: '40%' },
    { name: 'Subway', domain: 'subway.com', count: 63, x: '52%', y: '30%' },
    { name: 'Chick-fil-A', domain: 'chick-fil-a.com', count: 42, x: '75%', y: '58%' },
    { name: 'Jack in the Box', domain: 'jackinthebox.com', count: 22, x: '38%', y: '58%' },
  ];
  return (
    <div className="preview-map">
      <div className="preview-map-bg">
        <div className="map-block" style={{ top: '5%', left: '2%', width: '28%', height: '35%' }} />
        <div className="map-block" style={{ top: '5%', left: '55%', width: '42%', height: '30%' }} />
        <div className="map-block" style={{ top: '55%', left: '0%', width: '22%', height: '40%' }} />
        <div className="map-block" style={{ top: '48%', left: '55%', width: '20%', height: '48%' }} />
        <div className="map-block" style={{ top: '60%', left: '80%', width: '18%', height: '35%' }} />
        <div className="preview-map-road map-road-h" style={{ top: '42%' }} />
        <div className="preview-map-road map-road-h" style={{ top: '92%' }} />
        <div className="preview-map-road map-road-v" style={{ left: '32%' }} />
        <div className="preview-map-road map-road-v" style={{ left: '52%' }} />
        <div className="preview-map-road map-road-v" style={{ left: '78%' }} />
        <div className="preview-map-road map-road-h map-road-minor" style={{ top: '15%', left: '32%', width: '20%' }} />
        <div className="preview-map-road map-road-h map-road-minor" style={{ top: '70%', left: '25%', width: '28%' }} />
      </div>
      {pins.map((pin, i) => (
        <motion.div
          key={pin.domain}
          className="preview-map-pin"
          style={{ left: pin.x, top: pin.y }}
          initial={{ opacity: 0, y: -20, scale: 0 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 + i * 0.12, type: 'spring', stiffness: 300, damping: 15 }}
        >
          <div className="pin-logo-img">
            <img src={logoUrl(pin.domain, 64)} alt={pin.name} loading="lazy" />
          </div>
          <div className="pin-count">{pin.count}</div>
        </motion.div>
      ))}
      <motion.div
        className="preview-map-you"
        style={{ left: '32%', top: '48%' }}
        initial={{ scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8, type: 'spring' }}
      >
        <div className="you-dot"></div>
        <div className="you-pulse"></div>
      </motion.div>
    </div>
  );
};

const ComparePreview = () => {
  const meals = [
    { name: 'grilled nuggets', domain: 'chick-fil-a.com', cal: '130', p: '25', f: '3', c: '1', winner: true },
    { name: '10pc mcnuggets', domain: 'mcdonalds.com', cal: '410', p: '24', f: '24', c: '26', winner: false },
  ];
  return (
    <div className="preview-compare">
      {meals.map((m, i) => (
        <div
          key={m.name}
          className={`preview-compare-card ${m.winner ? 'winner' : ''}`}
        >
          {m.winner && <div className="best-pick-border"><div className="best-pick-spinner" /></div>}
          <div className="compare-logo">
            <img src={logoUrl(m.domain, 64)} alt={m.name} loading="lazy" />
          </div>
          <div className="compare-meal">{m.name}</div>
          <MacroPills cal={m.cal} p={m.p} f={m.f} c={m.c} delay={0.4 + i * 0.15} />
          {m.winner && <div className="compare-badge">best pick</div>}
        </div>
      ))}
    </div>
  );
};

const RecipePreview = () => (
  <motion.div
    className="preview-recipe recipe-shimmer"
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: 0.3 }}
  >
    <div className="preview-recipe-image">
      <img src={turkeyChiliImg} alt="turkey chili" />
    </div>
    <div className="preview-recipe-info">
      <motion.div
        className="preview-recipe-badge"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
      >
        main dish
      </motion.div>
      <div className="preview-recipe-name">turkey chili</div>
      <MacroPills cal="337" p="30" f="6.8" c="37" delay={0.5} />
    </div>
  </motion.div>
);

// Mockup Showcase Component
const MockupShowcase = ({ image, title, subtitle, features, reversed, gradient }) => {
  return (
    <div className={`mockup-showcase ${reversed ? 'reversed' : ''}`}>
      <motion.div
        className="showcase-content"
        initial={{ opacity: 0, x: reversed ? 50 : -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <span className="showcase-label" style={{ background: gradient }}>{subtitle}</span>
        <h2>{title}</h2>
        <ul className="showcase-features">
          {features.map((f, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + i * 0.1 }}
            >
              <span className="check-icon">✓</span>
              {f}
            </motion.li>
          ))}
        </ul>
      </motion.div>
      <motion.div
        className="showcase-mockup"
        initial={{ opacity: 0, y: 40, rotateY: reversed ? -10 : 10 }}
        whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="mockup-glow" style={{ background: gradient }}></div>
        <img src={image} alt={title} />
      </motion.div>
    </div>
  );
};

// Food Celebration Animation Component
const FoodCelebration = ({ show, onComplete }) => {
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    if (show) {
      // Generate random particles
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        Icon: celebrationIcons[Math.floor(Math.random() * celebrationIcons.length)],
        x: Math.random() * 200 - 100, // -100 to 100
        y: Math.random() * -150 - 50, // -50 to -200 (upward)
        rotation: Math.random() * 360,
        scale: 0.6 + Math.random() * 0.6,
        delay: Math.random() * 0.3,
      }));
      setParticles(newParticles);
      
      // Clear after animation
      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [show, onComplete]);
  
  return (
    <AnimatePresence>
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="celebration-particle"
          initial={{ 
            opacity: 1, 
            x: 0, 
            y: 0, 
            scale: 0,
            rotate: 0 
          }}
          animate={{ 
            opacity: [1, 1, 0],
            x: particle.x,
            y: particle.y,
            scale: [0, particle.scale, particle.scale * 0.8],
            rotate: particle.rotation
          }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: 1.2,
            delay: particle.delay,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <particle.Icon />
        </motion.div>
      ))}
    </AnimatePresence>
  );
};

// Subscription Form Component
const SubscriptionForm = ({ variant = 'default', highlight = false }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [showCelebration, setShowCelebration] = useState(false);
  const inputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!email.trim()) {
      setMessage('drop your email first');
      setMessageType('error');
      return;
    }
    
    if (!isValidEmail(email)) {
      setMessage('hmm, that doesn\'t look like a real email');
      setMessageType('error');
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      const response = await fetch('/.netlify/functions/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
        }),
      });
      
      const data = await response.json().catch(() => ({}));
      
      if (response.ok && data.success) {
        if (data.alreadySubscribed) {
          setMessage('you\'re already on the list! 🎉');
        } else {
          setMessage('welcome to the munch fam 🎉');
        }
        setMessageType('success');
        setEmail('');
        setShowCelebration(true);
      } else {
        setMessage('something went wrong. try again?');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      if (window.location.hostname === 'localhost') {
        setMessage('local testing needs netlify cli — run: npx netlify dev');
        setMessageType('error');
      } else {
        setMessage('something went wrong. try again?');
        setMessageType('error');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const isLarge = variant === 'large';
  const inputClass = isLarge ? 'subscription-input input-large' : 'subscription-input';
  const buttonClass = isLarge ? 'btn-primary btn-large' : 'btn-primary';

  // Focus input when highlighted
  useEffect(() => {
    if (highlight && inputRef.current) {
      inputRef.current.focus();
    }
  }, [highlight]);
  
  return (
    <form className="subscription-form" onSubmit={handleSubmit}>
      <div className={`subscription-input-wrapper ${highlight ? 'highlight-pulse' : ''}`}>
        <input
          ref={inputRef}
          type="email"
          className={inputClass}
          placeholder="enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          required
        />
        <div className="btn-celebration-wrapper">
          <motion.button
            type="submit"
            className={buttonClass}
            whileHover={isLoading ? {} : { scale: 1.05, boxShadow: isLarge ? "0 25px 50px rgba(232, 106, 51, 0.5)" : "0 20px 40px rgba(232, 106, 51, 0.4)" }}
            whileTap={isLoading ? {} : { scale: 0.95 }}
            disabled={isLoading}
          >
            {isLoading ? 'joining...' : 'i\'m in'}
          </motion.button>
          <FoodCelebration 
            show={showCelebration} 
            onComplete={() => setShowCelebration(false)} 
          />
        </div>
      </div>
      {message && (
        <motion.div
          className={`subscription-message ${messageType}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {message}
        </motion.div>
      )}
    </form>
  );
};

function App() {
  const { theme, toggleTheme } = useTheme();
  const heroRef = useRef(null);
  const [highlightEmail, setHighlightEmail] = useState(false);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const isMobile = useMobileMode();
  const heroY = useTransform(scrollYProgress, [0, 1], [0, isMobile ? 0 : 100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, isMobile ? 1 : 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, isMobile ? 1 : 0.95]);

  const scrollToEmail = () => {
    if (isMobile) {
      const form = document.querySelector('.hero .subscription-form');
      if (form) {
        const y = form.getBoundingClientRect().top + window.scrollY - window.innerHeight / 2 + form.offsetHeight / 2;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setTimeout(() => {
      setHighlightEmail(true);
      setTimeout(() => setHighlightEmail(false), 2000);
    }, 500);
  };

  return (
    <div className="App" data-theme={theme}>
      {/* Floating Nav */}
      <motion.nav
        className="floating-nav"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ delay: 0.5, type: "spring", stiffness: 100 }}
      >
        <div className="nav-inner">
          <div className="nav-logo">
            <img src={process.env.PUBLIC_URL + '/munch-logo.png'} alt="munch" />
            <span>munch</span>
          </div>
          <div className="nav-links">
            <a href="#features">features</a>
            <a href="#discover">discover</a>
            <a href="#map">map</a>
            <a href="#recipes">recipes</a>
          </div>
          <div className="nav-actions">
            <motion.button
              className="theme-toggle"
              onClick={toggleTheme}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Toggle theme"
            >
              <AnimatePresence mode="wait">
                {theme === 'dark' ? (
                  <motion.span
                    key="sun"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    ☀️
                  </motion.span>
                ) : (
                  <motion.span
                    key="moon"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    🌙
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
            <motion.button
              onClick={scrollToEmail}
              className="nav-download"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="nav-download-border"></div>
              join waitlist
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* HERO SECTION */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        
        {/* Floating Food Icons */}
        <div className="hero-floating-icons">
          <div className="hero-icon hero-icon-1"><TacoIcon /></div>
          <div className="hero-icon hero-icon-2"><SushiIcon /></div>
          <div className="hero-icon hero-icon-3"><IceCreamIcon /></div>
          <div className="hero-icon hero-icon-4"><CroissantIcon /></div>
          <div className="hero-icon hero-icon-5"><AvocadoIcon /></div>
          <div className="hero-icon hero-icon-6"><CakeIcon /></div>
        </div>
        

        <motion.div
          className="hero-content"
          style={{ y: heroY, opacity: heroOpacity, scale: heroScale }}
        >
          <motion.div
            className="hero-upper"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <span className="badge-dot"></span>
              coming soon to ios
            </motion.div>
            
            <h1>
              <span className="hero-line gradient-text">feed </span>
              <span className="hero-line">your goals.</span>
            </h1>
            
            <p className="hero-description">
              the easiest way to find meals that match your goals — no matter where you are.
            </p>
          </motion.div>

          <motion.div
            className="hero-phone"
            initial={{ opacity: 0, y: 60, rotateY: -15 }}
            animate={{ opacity: 1, y: 0, rotateY: 0 }}
            transition={{ duration: 1, delay: 0.4, type: "spring" }}
          >
            <div className="phone-glow"></div>
            <img src={heroMockup} alt="munch app" />
          </motion.div>

          <motion.div
            className="hero-lower"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <p className="subscription-text">be first to know when we launch — plus sneak peeks, health tips & more</p>
            <SubscriptionForm highlight={highlightEmail} />
            <div className="social-links-hero">
              <a href="https://www.reddit.com/r/munchapp/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Reddit">
                <i className="fa-brands fa-reddit-alien"></i>
              </a>
              <a href="https://www.instagram.com/trymunch.app/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
                <i className="fa-brands fa-instagram"></i>
              </a>
              <a href="https://x.com/trymunchapp" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="X">
                <i className="fa-brands fa-x-twitter"></i>
              </a>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="scroll-hint"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
        >
          <span>scroll to explore</span>
          <div className="scroll-line" />
        </motion.div>
      </section>

      {/* Brand Logo Marquee */}
      <div className="icon-marquee-wrapper">
        <div className="icon-marquee">
          <div className="marquee-track">
            {[...Array(3)].map((_, setIndex) => (
              <div className="marquee-content" key={setIndex}>
                {brandLogos.map((b) => (
                  <div className="marquee-item brand-logo-item" key={b.domain}>
                    <img
                      src={logoUrl(b.domain, 128)}
                      alt={b.name}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* STATS BAR */}
      <section className="stats-bar">
        <div className="stats-inner">
          <motion.div
            className="stat"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="stat-value"><AnimatedCounter value="1000000" suffix="+" /></span>
            <span className="stat-label">restaurants</span>
          </motion.div>
          <div className="stat-divider"></div>
          <motion.div
            className="stat"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <span className="stat-value"><AnimatedCounter value="50000" suffix="+" /></span>
            <span className="stat-label">verified menu items</span>
          </motion.div>
          <div className="stat-divider"></div>
          <motion.div
            className="stat"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <span className="stat-value"><AnimatedCounter value="1000" suffix="+" /></span>
            <span className="stat-label">recipes</span>
          </motion.div>
          <div className="stat-divider"></div>
          <motion.div
            className="stat"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <span className="stat-value"><AnimatedCounter value="30" suffix="+" /></span>
            <span className="stat-label">cuisines</span>
          </motion.div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="features" id="features">
        <div className="features-header">
          <motion.span
            className="section-tag"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            why munch?
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <span className="gradient-text">eat smarter.</span><br className="mobile-br" /> not harder.
          </motion.h2>
        </div>

        <div className="features-grid">
          <FeatureCard
            preview={<MacroPreview />}
            title="track macros"
            description="real nutrition info for every meal. no guessing, no googling."
            index={0}
          />
          <FeatureCard
            preview={<FiltersPreview />}
            title="personalized plans"
            description="tell us your goals and we'll show you exactly what to eat. it's that easy."
            index={1}
          />
          <FeatureCard
            preview={<SearchChipsPreview />}
            title="smart search"
            description="filter by diet, allergies, or exact macro ranges in seconds."
            index={2}
          />
          <FeatureCard
            preview={<MapPinsPreview />}
            title="1,000,000+ restaurants"
            description="fast food to fine dining — we've got the nutrition data for all of them."
            index={3}
          />
          <FeatureCard
            preview={<ComparePreview />}
            title="meal comparisons"
            description="compare the same dish across restaurants. pick the one that hits different."
            index={4}
          />
          <FeatureCard
            preview={<RecipePreview />}
            title="healthy recipes"
            description="don't feel like going out? we've got macro-friendly recipes too."
            index={5}
          />
        </div>
      </section>

      {/* MAP SHOWCASE */}
      <section className="showcase-section dark-section" id="map">
        <MockupShowcase
          image={mapMockup}
          title={<>find healthy options<br/><span className="gradient-text">wherever you are</span></>}
          subtitle="map view"
          features={[
            "every restaurant near you, right on the map",
            "each pin shows how many meals actually fit your goals",
            "filter the whole map by your cal, protein, fat & carb targets",
            "tap any spot to instantly see their menu ranked by your macros"
          ]}
          gradient="linear-gradient(135deg, #10B981 0%, #059669 100%)"
        />
      </section>

      {/* DISCOVER SHOWCASE */}
      <section className="showcase-section" id="discover">
        <MockupShowcase
          image={discoverMockup}
          title={<>discover the best meals<br/><span className="gradient-text">ranked for your goals</span></>}
          subtitle="discover"
          features={[
            "top meals from every nearby spot, ranked by your macros",
            "filter by protein — chicken, beef, pork & lamb, you name it",
            "cal, protein, fat, carbs — all right there for every dish",
            "chick-fil-a, chili's, and thousands more. we've got them all"
          ]}
          reversed
          gradient="linear-gradient(135deg, #FF6B35 0%, #F7931A 100%)"
        />
      </section>

      {/* AI NUTRITION SHOWCASE */}
      <section className="showcase-section" id="ai-nutrition">
        <MockupShowcase
          image={aiRestaurantMockup}
          title={<>ai-powered nutrition<br/><span className="gradient-text">for any restaurant</span></>}
          subtitle="ai estimates"
          features={[
            "get nutrition data for any restaurant — even the ones that don't post it",
            "our ai breaks down every dish: cal, protein, fat & carbs",
            "sparkle icon = ai estimate. no surprises, no confusion",
            "never skip a spot just because they don't share nutrition info"
          ]}
          gradient="linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)"
        />
      </section>

      {/* PLATE CALCULATOR SHOWCASE */}
      <section className="showcase-section alt-section" id="plate">
        <MockupShowcase
          image={plateMockup}
          title={<>build your plate,<br/><span className="gradient-text">track your day</span></>}
          subtitle="plate calculator"
          features={[
            "add items to your plate, see exactly how it hits your daily goals",
            "progress bars show if you're on track — or going a little overboard",
            "sync it all to apple health with one tap",
            "know exactly what's left in your budget before you even order"
          ]}
          reversed
          gradient="linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)"
        />
      </section>

      {/* RECIPES SHOWCASE */}
      <section className="showcase-section" id="recipes">
        <MockupShowcase
          image={recipesMockup}
          title={<>home cooking,<br/><span className="gradient-text">made healthy</span></>}
          subtitle="recipes"
          features={[
            "hundreds of macro-friendly recipes with full nutrition info",
            "breakfast, lunch, dinner, snacks — filter by whatever you're craving",
            "every recipe ranked to match your nutrition goals",
            "chicken salad, turkey chili, the works — real meals, real macros"
          ]}
          gradient="linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)"
        />
      </section>

      {/* PERSONALIZATION SHOWCASE */}
      <section className="showcase-section dark-section" id="personalization">
        <MockupShowcase
          image={settingsMockup}
          title={<>your goals,<br/><span className="gradient-text">your rules</span></>}
          subtitle="personalization"
          features={[
            "set your own nutrition filters — max cals, min protein, whatever you want",
            "dial in your daily macro targets for cal, protein, fat & carbs",
            "pick your diet preferences and customize how results show up",
            "sync with apple health so everything lives in one place"
          ]}
          reversed
          gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
        />
      </section>
      {/* CTA SECTION */}
      <section className="cta-section">
        <div className="cta-bg">
          <div className="cta-icon cta-icon-1"><BurgerIcon /></div>
          <div className="cta-icon cta-icon-2"><PizzaIcon /></div>
          <div className="cta-icon cta-icon-3"><DonutIcon /></div>
          <div className="cta-icon cta-icon-4"><CoffeeIcon /></div>
        </div>
        
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>want lunch?<br className="mobile-br" /> try munch.</h2>
          <p>drop your email and we'll let you know when it's go time.</p>
          
          <SubscriptionForm variant="large" />
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-top">
          <div className="footer-brand">
            <img src={process.env.PUBLIC_URL + '/munch-logo.png'} alt="munch" />
            <span>munch</span>
          </div>
          <div className="footer-links">
            <div className="footer-col">
              <h4>product</h4>
              <a href="#features">features</a>
              <a href="#discover">discover</a>
              <a href="#map">map</a>
              <a href="#recipes">recipes</a>
            </div>
            <div className="footer-col">
              <h4>company</h4>
              <span className="footer-coming-soon">about us</span>
              <span className="footer-coming-soon">careers</span>
              <span className="footer-coming-soon">blog</span>
              <span className="footer-coming-soon">press</span>
            </div>
            <div className="footer-col">
              <h4>support</h4>
              <span className="footer-coming-soon">help center</span>
              <a href="mailto:abehzad.developer@gmail.com">contact support</a>
              <a href="/privacy">privacy</a>
              <span className="footer-coming-soon">terms</span>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <p>© 2026 munch. all rights reserved.</p>
            <a
              className="fatsecret-attribution"
              href="https://www.platform.fatsecret.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Nutrition information provided by fatsecret Platform API"
            >
              <img
                className="fatsecret-badge fatsecret-badge-dark-theme"
                src="https://platform.fatsecret.com/api/static/images/powered_by_fatsecret_horizontal_white.svg"
                alt="Nutrition information provided by fatsecret Platform API"
              />
              <img
                className="fatsecret-badge fatsecret-badge-light-theme"
                src="https://platform.fatsecret.com/api/static/images/powered_by_fatsecret_horizontal_dark.svg"
                alt="Nutrition information provided by fatsecret Platform API"
              />
            </a>
          </div>
          <div className="social-links-hero">
            <a href="https://www.reddit.com/r/munchapp/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Reddit">
              <i className="fa-brands fa-reddit-alien"></i>
            </a>
            <a href="https://www.instagram.com/trymunch.app/" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
              <i className="fa-brands fa-instagram"></i>
            </a>
            <a href="https://x.com/trymunchapp" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="X">
              <i className="fa-brands fa-x-twitter"></i>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
